'use client';
import { useEffect, useRef, useState } from 'react';
import { mountTurnstile, type TurnstileHandle } from './turnstile.mjs';

type Props = {
  siteKey: string; onToken: (token: string) => void; resetKey?: number;
  action?: string; nonce?: string; complete?: boolean; onRestart?: () => void;
};
export function TurnstileField({ siteKey, onToken, resetKey = 0, action = 'lead', nonce, complete = false, onRestart }: Props) {
  const element = useRef<HTMLDivElement>(null);
  const handle = useRef<TurnstileHandle | null>(null);
  const callback = useRef(onToken);
  useEffect(() => { callback.current = onToken; });
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const spanish = typeof document !== 'undefined' && document.documentElement.lang.startsWith('es');
  useEffect(() => {
    if (complete || !element.current) return;
    const cancel = new AbortController();
    let widget: TurnstileHandle | null = null;
    mountTurnstile(element.current, { siteKey, action, nonce, signal: cancel.signal,
      onToken: token => { if (!cancel.signal.aborted) callback.current(token); },
      onError: message => { if (!cancel.signal.aborted) setError(message); },
      onPending: value => { if (!cancel.signal.aborted) setPending(value); },
    }).then(mounted => {
      if (cancel.signal.aborted) mounted.destroy(); else { widget = mounted; handle.current = mounted; }
    }).catch(() => {});
    return () => { cancel.abort(); widget?.destroy(); if (handle.current === widget) handle.current = null; };
  }, [siteKey, action, nonce, attempt, complete]);
  useEffect(() => { if (!complete) handle.current?.reset(); }, [resetKey, complete]);
  if (complete) return onRestart ? <button type="button" onClick={onRestart}>{spanish ? 'Enviar otra solicitud' : 'Send another request'}</button> : null;
  return <div>
    <div ref={element} />
    <p role="status" aria-live="polite">{error ? (spanish ? 'No se pudo completar la verificación. Inténtelo de nuevo.' : error) : pending ? (spanish ? 'Verificando…' : 'Checking…') : ''}</p>
    {error && <button type="button" onClick={() => { if (handle.current) handle.current.reset(); else setAttempt(n => n + 1); }}>{spanish ? 'Reintentar verificación' : 'Retry verification'}</button>}
  </div>;
}
